// SPDX-License-Identifier: BUSL-1.1

/**
 * VmPoolTable — displays active runner VMs for a tenant.
 *
 * Shows: server ID, status badge, age, associated job, cost estimate.
 * Data fetched from /api/tenant/vms.
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { DataTable } from '@/components/data-table';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';

interface VmRecord {
  serverId: string;
  createdAt: string;
  status: string;
  jobId?: string;
  provider: string;
}

// ---------------------------------------------------------------------------
// Status badge color map
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, string> = {
  provisioning: 'bg-blue-900/30 text-blue-400',
  ready: 'bg-green-900/30 text-green-400',
  running: 'bg-emerald-900/30 text-emerald-400',
  completed: 'bg-neutral-800 text-neutral-400',
  drained: 'bg-neutral-800 text-neutral-500',
};

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

const columns: ColumnDef<VmRecord>[] = [
  {
    accessorKey: 'serverId',
    header: 'Server ID',
    cell: ({ row }) => (
      <span className="font-mono text-xs text-neutral-300">{row.getValue('serverId')}</span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('status') as string;
      const colorClass = STATUS_COLORS[status] ?? 'bg-neutral-800 text-neutral-400';
      return (
        <Badge className={`rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}>
          {status}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Age',
    cell: ({ row }) => {
      const createdAt = new Date(row.getValue('createdAt'));
      const ageMs = Date.now() - createdAt.getTime();
      const ageMins = Math.floor(ageMs / 60000);
      const ageHours = Math.floor(ageMins / 60);

      let label: string;
      if (ageMins < 1) label = 'just now';
      else if (ageMins < 60) label = `${ageMins}m`;
      else if (ageHours < 24) label = `${ageHours}h ${ageMins % 60}m`;
      else label = `${Math.floor(ageHours / 24)}d ${ageHours % 24}h`;

      return <span className="text-sm text-neutral-400">{label}</span>;
    },
  },
  {
    accessorKey: 'jobId',
    header: 'Job ID',
    cell: ({ row }) => {
      const jobId = row.getValue('jobId') as string | undefined;
      if (!jobId) return <span className="text-xs text-neutral-600">—</span>;
      return <span className="font-mono text-xs text-neutral-400">#{jobId}</span>;
    },
  },
  {
    accessorKey: 'provider',
    header: 'Provider',
    cell: ({ row }) => (
      <span className="text-sm text-neutral-400 capitalize">{row.getValue('provider')}</span>
    ),
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface VmPoolTableProps {
  className?: string;
}

export function VmPoolTable({ className }: VmPoolTableProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['tenant-vms'],
    queryFn: () => apiFetch<{ vms: VmRecord[]; totalCostUsd: number }>('/tenant/vms'),
    refetchInterval: 30_000, // refresh every 30s so cost estimates stay fresh
  });

  const vms = data?.vms ?? [];

  return (
    <div className={className}>
      <DataTable
        columns={columns}
        data={vms}
        emptyMessage="No active VMs. Trigger a workflow with runs-on: [self-hosted, hetzner-runner] to provision your first runner."
        pageSizeOptions={[10, 25, 50]}
      />
    </div>
  );
}